/**
 * Object Editing Plugin
 *
 * Handles inline editing for object node fields. Each text-based field
 * value becomes contentEditable when clicked. Supports:
 * - Click a field value to edit it
 * - Tab / Shift+Tab to move between editable fields
 * - Escape to cancel editing
 * - Enter to save (Shift+Enter for newline in long-text fields)
 * - Blur saves automatically
 *
 * Non-editable field types (images, booleans) are skipped.
 */

import type {
	EditingPlugin,
	EditingPluginContext,
	EditingSession,
	EditingResult,
} from '../editing';

/** Field types that support inline editing */
const EDITABLE_FIELD_TYPES = new Set([
	'text',
	'long-text',
	'markdown',
	'url',
	'number',
	'json',
]);

/**
 * Create an object-node editing plugin.
 *
 * Object nodes have multiple fields, each rendered with a `data-field-type`
 * attribute. This plugin makes editable fields' values contentEditable.
 */
export function createObjectEditingPlugin(): EditingPlugin {
	return {
		nodeTypes: ['object'],
		editableSelector: '.object-fields',

		startEditing(ctx: EditingPluginContext): EditingSession {
			const { element: fieldsContainer, input } = ctx;

			// Collect original values for cancel support
			const originalValues = new Map<HTMLElement, string>();
			const editableElements: HTMLElement[] = [];

			// Find all editable field values
			const fieldDivs = fieldsContainer.querySelectorAll('.object-field');
			for (const fieldDiv of fieldDivs) {
				const fieldType = (fieldDiv as HTMLElement).dataset.fieldType;
				if (!fieldType || !EDITABLE_FIELD_TYPES.has(fieldType)) continue;

				const valueEl = fieldDiv.querySelector('.object-field-value') as HTMLElement;
				if (!valueEl) continue;

				editableElements.push(valueEl);
				originalValues.set(valueEl, valueEl.innerHTML);
			}

			if (editableElements.length === 0) {
				// Nothing editable — return a no-op session
				return {
					getContent: () => '',
					stop: () => ({ content: '' }),
				};
			}

			// Push editing mode
			input.pushMode('editing');

			let stopped = false;
			let activeElement: HTMLElement | null = null;

			// Make a field value editable
			function activateField(valueEl: HTMLElement) {
				if (activeElement === valueEl) return;
				deactivateField(); // save previous

				activeElement = valueEl;
				valueEl.contentEditable = 'true';
				valueEl.classList.add('object-field-editing');
				valueEl.focus();

				// Place cursor at end
				const range = document.createRange();
				const sel = window.getSelection();
				range.selectNodeContents(valueEl);
				range.collapse(false);
				sel?.removeAllRanges();
				sel?.addRange(range);
			}

			// Deactivate current field (but stay in edit mode)
			function deactivateField() {
				if (!activeElement) return;
				activeElement.contentEditable = 'false';
				activeElement.classList.remove('object-field-editing');
				activeElement = null;
			}

			// Navigate to next/prev editable field
			function navigateField(direction: 1 | -1) {
				if (!activeElement) return;
				const idx = editableElements.indexOf(activeElement);
				const next = idx + direction;
				if (next >= 0 && next < editableElements.length) {
					activateField(editableElements[next]);
				}
			}

			// Collect all current field values as serialised content
			function getContent(): string {
				// Build a key→value map from the DOM
				const result: Record<string, string> = {};
				for (const valueEl of editableElements) {
					const fieldDiv = valueEl.closest('.object-field') as HTMLElement;
					const keyEl = fieldDiv?.querySelector('.object-field-key');
					if (keyEl) {
						result[keyEl.textContent ?? ''] = valueEl.innerText ?? '';
					}
				}
				return JSON.stringify(result);
			}

			// ---- Event handlers ----

			function onFieldClick(e: MouseEvent) {
				if (stopped) return;
				const target = e.target as HTMLElement;
				// Walk up to find .object-field-value
				let valueEl = target.closest('.object-field-value') as HTMLElement;
				if (!valueEl) return;
				// Check if parent field is editable
				const fieldDiv = valueEl.closest('.object-field') as HTMLElement;
				const fieldType = fieldDiv?.dataset.fieldType;
				if (!fieldType || !EDITABLE_FIELD_TYPES.has(fieldType)) return;
				if (!editableElements.includes(valueEl)) return;

				e.stopPropagation();
				activateField(valueEl);
			}

			function onKeydown(e: KeyboardEvent) {
				if (stopped) return;
				e.stopPropagation();

				if (e.key === 'Escape') {
					e.preventDefault();
					ctx.requestStop(false);
				} else if (e.key === 'Tab') {
					e.preventDefault();
					navigateField(e.shiftKey ? -1 : 1);
				} else if (e.key === 'Enter' && !e.shiftKey) {
					// For single-line fields (text, url, number), Enter saves
					const fieldDiv = activeElement?.closest('.object-field') as HTMLElement;
					const fieldType = fieldDiv?.dataset.fieldType;
					if (fieldType === 'text' || fieldType === 'url' || fieldType === 'number') {
						e.preventDefault();
						ctx.requestStop(true);
					}
					// For long-text, markdown, json — Enter inserts newline (default)
				}
			}

			function onBlur(e: FocusEvent) {
				if (stopped) return;
				// Check if focus moved to another editable field within the same node
				const related = e.relatedTarget as HTMLElement | null;
				if (related && fieldsContainer.contains(related)) return;
				// Small delay to allow click-to-activate-another-field to fire first
				setTimeout(() => {
					if (stopped) return;
					if (activeElement && !fieldsContainer.contains(document.activeElement)) {
						ctx.requestStop(true);
					}
				}, 100);
			}

			// Register event listeners
			fieldsContainer.addEventListener('click', onFieldClick);
			fieldsContainer.addEventListener('keydown', onKeydown);
			fieldsContainer.addEventListener('focusout', onBlur);

			// Activate the first editable field immediately
			activateField(editableElements[0]);

			function stopSession(save: boolean): EditingResult {
				if (stopped) return { content: save ? getContent() : '' };
				stopped = true;

				// Remove listeners
				fieldsContainer.removeEventListener('click', onFieldClick);
				fieldsContainer.removeEventListener('keydown', onKeydown);
				fieldsContainer.removeEventListener('focusout', onBlur);

				const content = save ? getContent() : '';

				// Restore original HTML if cancelling
				if (!save) {
					for (const [el, html] of originalValues) {
						el.innerHTML = html;
					}
				}

				// Deactivate all fields
				for (const el of editableElements) {
					el.contentEditable = 'false';
					el.classList.remove('object-field-editing');
				}
				activeElement = null;

				// Pop editing mode
				input.popMode('editing');

				return { content };
			}

			return {
				getContent,
				stop: (save: boolean) => stopSession(save),
			};
		},
	};
}
